import React, { useMemo, useState } from 'react'

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import { docOptions } from '@/features/offers/constants/options'
import { hidePrintOfferModal } from '@/stores/offers/offersSlice'
import { selectPrintOfferModal } from '@/stores/offers/printOfferModal'
import { useDispatch, useSelector } from 'react-redux'
import useOffer from '@/features/offers/hooks/useOffer'
import { OfferDocumentTypes } from '@/features/offers/constants'
import config from '@/config'

const TabOptionGroup = ({ tab, setCheckedTabs, checkedTabs }) => {
  if (Array.isArray(tab.children) && tab.children.length > 0) {
    const childValues = tab.children.map((child) => child.value)
    const checkedChildrenCount = childValues.filter((val) => checkedTabs.includes(val)).length
    const allChecked = checkedChildrenCount === childValues.length
    const isIndeterminate = checkedChildrenCount > 0 && !allChecked

    return (
      <>
        <Checkbox
          key={tab.value}
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          onChange={() => {
            if (allChecked) {
              // Remove all children
              setCheckedTabs((prev) => prev.filter((t) => !childValues.includes(t)))
            } else {
              // Add all children
              setCheckedTabs((prev) => [...new Set([...prev, ...childValues])])
            }
          }}
        >
          {tab.label}
        </Checkbox>
        <Stack pl={6} mt={1} spacing={1}>
          {tab.children.map((childTab) => (
            <Checkbox
              key={childTab.value}
              value={childTab.value}
              isChecked={checkedTabs.includes(childTab.value)}
              onChange={(e) => {
                if (e.target.checked) {
                  setCheckedTabs((prev) => [...prev, childTab.value])
                } else {
                  setCheckedTabs((prev) => prev.filter((t) => t !== childTab.value))
                }
              }}
            >
              {childTab.label}
            </Checkbox>
          ))}
        </Stack>
      </>
    )
  }

  return (
    <Checkbox
      key={tab.value}
      value={tab.value}
      isChecked={checkedTabs.includes(tab.value)}
      onChange={(e) => {
        if (e.target.checked) {
          setCheckedTabs((prev) => [...prev, tab.value])
        } else {
          setCheckedTabs((prev) => prev.filter((t) => t !== tab.value))
        }
      }}
    >
      {tab.label}
    </Checkbox>
  )
}

const PrintModal = () => {
  const { isOpened, subSystemNames, offerId } = useSelector(selectPrintOfferModal)

  const [checkedTabs, setCheckedTabs] = useState([])
  const [otherDocs, setOtherDocs] = useState([])

  const [checkedDocs, setCheckedDocs] = useState(docOptions.map((option) => option.value))

  const dispatch = useDispatch()
  const { useGetMergedDocument, useFetchOffer } = useOffer()
  const getMergedDocument = useGetMergedDocument()

  const { data: offer } = useFetchOffer(offerId)

  const getLatestFileId = (document) => {
    if (!document.files?.length) return undefined
    const maxVersion = Math.max(...document.files.map((f) => f.version ?? 0))
    return document.files.find((f) => f.version === maxVersion)?._id
  }

  // merged docs
  const docOptionsMerged = useMemo(() => {
    const externalOthers =
      offer?.documents?.externalOthers?.map((document, index) => {
        const latestFileVersion = getLatestFileId(document)
        return {
          value: `${OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS}_${index + 1}`,
          label: `1.${5 + index} ${document.name}`,
          fileId: latestFileVersion,
        }
      }) ?? []

    const internalOthers =
      offer?.documents?.internalOthers?.map((document, index) => {
        const latestFileVersion = getLatestFileId(document)
        return {
          value: `${OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS}_${index + 1}`,
          label: `2.${13 + index} ${document.name}`,
          fileId: latestFileVersion,
        }
      }) ?? []

    const merged = [...docOptions, ...externalOthers, ...internalOthers]

    const idxExternal = merged.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)
    const idxInternal = merged.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)

    let filtered = [...merged]

    if (idxExternal !== -1) {
      filtered.splice(idxExternal, 1)
    }

    if (idxInternal !== -1) {
      const newIdxInternal = filtered.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)
      if (newIdxInternal !== -1) {
        filtered.splice(newIdxInternal, 1)
      }
    }
    // otherDocs is array of fileId
    setOtherDocs([
      ...externalOthers.flatMap((opt) => (opt.fileId ? [opt.fileId] : [])),
      ...internalOthers.flatMap((opt) => (opt.fileId ? [opt.fileId] : [])),
    ])
    setCheckedDocs((prev) => {
      const withoutBaseDocs = prev.filter(
        (val) =>
          val !== OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS && val !== OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
      )

      return [
        ...new Set([
          ...withoutBaseDocs,
          ...(externalOthers?.map((opt) => opt.value) ?? []),
          ...(internalOthers?.map((opt) => opt.value) ?? []),
        ]),
      ]
    })

    return filtered.sort((a, b) => a.label.localeCompare(b.label))
  }, [offer, docOptions])

  const docAllChecked = checkedDocs.length === docOptionsMerged.length
  const docIsIndeterminate = checkedDocs.length > 0 && !docAllChecked

  //Translated
  const tabOptions = useMemo(() => {
    const subSystemTabs = subSystemNames.map((name, index) => {
      return {
        value: `subSystem-${index}`,
        label: `Subsystem 1.${index + 1} ${name}`,
      }
    })
    const handoverFormTab = config.handoverFormEnabled ? [{ value: 'handoverForm', label: 'Handover Form' }] : []

    return [
      { value: 'offerOverview', label: 'Offer Overview' },
      ...subSystemTabs,
      { value: 'commercialChecklist', label: 'Commercial Checklist' },
      {
        value: 'sourcing',
        label: 'Sourcing',
        children: [
          { value: 'sourcingGeneral', label: 'General' },
          { value: 'sourcingPM040', label: 'PM040' },
          { value: 'sourcingPM100', label: 'PM100' },
        ],
      },

      { value: 'legal', label: 'Legal' },
      { value: 'booking', label: 'Booking' },
      ...handoverFormTab,
      { value: 'costStructure', label: 'Cost Structure' },
      { value: 'history', label: 'History' },
    ]
  }, [subSystemNames])

  // Get all selectable tab values (expand children for parent tabs)
  const allSelectableTabValues = useMemo(() => {
    const values = []
    tabOptions.forEach((tab) => {
      if (Array.isArray(tab.children) && tab.children.length > 0) {
        // For parent tabs with children, add only child values
        values.push(...tab.children.map((child) => child.value))
      } else {
        // For regular tabs, add the tab value
        values.push(tab.value)
      }
    })
    return values
  }, [tabOptions])

  const tabAllChecked = checkedTabs.length === allSelectableTabValues.length
  const tabIsIndeterminate = checkedTabs.length > 0 && !tabAllChecked

  // Handler

  const closeModal = () => {
    setCheckedTabs(allSelectableTabValues)
    setCheckedDocs(docOptionsMerged.map((option) => option?.value))

    dispatch(hidePrintOfferModal())
  }

  const sanitizeCheckedDocs = (docs) => {
    const result = new Set()

    docs.forEach((doc) => {
      if (doc.startsWith(OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)) {
        result.add(OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)
      } else if (doc.startsWith(OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)) {
        result.add(OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)
      } else {
        result.add(doc)
      }
    })

    return Array.from(result)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    // startPrint(checkedTabs, checkedDocs);

    //Get the links of the download file.

    try {
      dispatch(showLoadingOverlay(true))

      const sanitizedDocs = sanitizeCheckedDocs(checkedDocs)
      const response = await getMergedDocument.mutateAsync({
        offerId,
        tabs: checkedTabs,
        fileTypes: sanitizedDocs,
        otherDocs: otherDocs,
      })
      const blob = new Blob([response], {
        type: 'application/pdf',
      })
      if (blob) {
        const fileUrl = URL.createObjectURL(blob)

        window.open(fileUrl)
      }
      closeModal()
    } catch (error) {
      return
    } finally {
      dispatch(hideLoadingOverlay(false))
    }
  }

  return (
    <Modal isOpen={isOpened} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW={'60%'}>
        <form onSubmit={onSubmit}>
          <ModalHeader>Print Offer</ModalHeader>

          <ModalCloseButton />
          <ModalBody>
            <Flex gap="2">
              <Box mb="2">
                <Checkbox
                  isChecked={tabAllChecked}
                  isIndeterminate={tabIsIndeterminate}
                  onChange={() => {
                    if (tabAllChecked) {
                      setCheckedTabs([])
                    } else {
                      setCheckedTabs(allSelectableTabValues)
                    }
                  }}
                >
                  Select Tabs
                </Checkbox>
                <CheckboxGroup colorScheme="blue" defaultValue={checkedTabs} value={checkedTabs}>
                  <Stack pl={6} mt={1} spacing={1}>
                    {tabOptions.map((tab) => (
                      <TabOptionGroup
                        key={tab.value}
                        tab={tab}
                        setCheckedTabs={setCheckedTabs}
                        checkedTabs={checkedTabs}
                      />
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
              <Box>
                <Checkbox
                  isChecked={docAllChecked}
                  isIndeterminate={docIsIndeterminate}
                  onChange={() => {
                    if (docAllChecked) {
                      setCheckedDocs([])
                    } else {
                      setCheckedDocs(docOptionsMerged.map((tab) => tab.value))
                    }
                  }}
                >
                  Select Documents
                </Checkbox>
                <CheckboxGroup colorScheme="blue" defaultValue={checkedDocs} value={checkedDocs}>
                  <Stack pl={6} mt={1} spacing={1}>
                    {docOptionsMerged.map((doc, index) => (
                      <Checkbox
                        key={doc.value + index}
                        value={doc.value}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (
                              doc.value.startsWith(
                                OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS ||
                                  OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
                              )
                            ) {
                              setOtherDocs((prev) => [...prev, doc.fileId])
                            }
                            setCheckedDocs((prev) => [...prev, doc.value])
                          } else {
                            if (
                              doc.value.startsWith(
                                OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS ||
                                  OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
                              )
                            ) {
                              setOtherDocs((prev) => prev.filter((d) => d !== doc.fileId))
                            }
                            setCheckedDocs((prev) => prev.filter((d) => d !== doc.value))
                          }
                        }}
                      >
                        {doc.label}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button colorScheme="green" type="submit">
                Confirm
              </Button>

              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default PrintModal
